import React, { Component } from "react";
import {
  Button,
  Container,
  Row,
  Form,
  Col,
  Offcanvas,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import configData from "../../config.json";
import { toRDTColumns } from "../common/ColumnTranslator";
import PermissionChecker from "../common/PermissionChecker";
import DataSourceManager from "../common/DataSourceManager";
import InteractionEngine from "../common/InteractionEngine";
import GroupedFormRenderer from "../common/GroupedFormRenderer";

/**
 * ReactDataTableAdapter - Renders react-data-table-component DataTable + search bar + Offcanvas CRUD form.
 * Translates columnsConfig via ColumnTranslator. Auto-appends sId/uId to API calls.
 * Integrates with PermissionChecker for RBAC: hides columns, masks fields, controls CRUD buttons.
 *
 * Props:
 *   - config: Parsed widget instance config with columnsConfig, actionsConfig, tableConfig, formConfig, etc.
 *   - pageRoute: Current page route
 *   - onEvent: Event callback for cross-widget communication
 */
export default class ReactDataTableAdapter extends Component {
  constructor(props) {
    super(props);
    const { config } = props;
    const tableConfig = config.tableConfig || {};
    const actionsConfig = config.actionsConfig || {};
    const resource = config.sourceTable || "";

    // RBAC: filter actionsConfig based on roleData permissions
    const filteredActions = PermissionChecker.filterActionsConfig(actionsConfig, resource);

    // RBAC: filter columnsConfig to remove columns the user can't view
    const filteredColumns = PermissionChecker.filterColumnsConfig(
      config.columnsConfig || [],
      resource
    );

    this.state = {
      show: false,
      isSubmitting: false,
      isEditing: false,
      data: [],
      filteredData: null,
      loading: true,
      currentPage: 1,
      itemsPerPage: tableConfig.paginationPerPage || 100,
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
      errors: {},
      formValues: {},
      editId: null,
      // DataSource state
      dataSourceOptions: {},
      // Interaction state
      fieldVisibility: {},   // { fieldName: true/false }
      fieldDisabled: {},     // { fieldName: true/false }
      fieldOptions: {},      // { fieldName: [{ value, label }] }
    };

    // Store RBAC-filtered actions for use in render
    this.filteredActions = filteredActions;
    this.resource = resource;
    this.dataSourceManager = null;
    this.dsUnsubscribers = [];
    this.interactionEngine = null;

    // Build columns from RBAC-filtered config
    this.tableClms = toRDTColumns(filteredColumns, {
      actionsConfig: filteredActions,
      onEdit: (row) => this.handleEditRow(row),
      onDelete: (row) => this.handleDelete(row),
    });
  }

  componentDidMount() {
    this.fetchData();
    this._initDataSources();
    this._initInteractions();
  }

  componentWillUnmount() {
    this.dsUnsubscribers.forEach((unsub) => unsub());
    if (this.dataSourceManager) {
      this.dataSourceManager.destroy();
    }
    if (this.interactionEngine) {
      this.interactionEngine.destroy();
    }
  }

  _initDataSources = () => {
    const { config } = this.props;
    const formConfig = config.formConfig || {};
    const dataSources = formConfig.dataSources || [];
    if (dataSources.length === 0) return;

    const fields = this._getAllFields(formConfig);
    this.dataSourceManager = new DataSourceManager(
      dataSources,
      this.state.sId,
      this.state.uId
    );

    // Subscribe for each field that references a data source
    fields.forEach((field) => {
      if (field.dataSource) {
        const unsub = this.dataSourceManager.subscribe(field.dataSource, (options) => {
          this.setState((prev) => ({
            dataSourceOptions: {
              ...prev.dataSourceOptions,
              [field.fieldName]: options,
            },
          }));
        });
        this.dsUnsubscribers.push(unsub);
      }
    });

    // Load all independent sources
    this.dataSourceManager.loadInitialSources().then((optionsMap) => {
      const dsOptions = {};
      fields.forEach((field) => {
        if (field.dataSource && optionsMap[field.dataSource]) {
          dsOptions[field.fieldName] = optionsMap[field.dataSource];
        }
      });
      this.setState((prev) => ({
        dataSourceOptions: { ...prev.dataSourceOptions, ...dsOptions },
      }));
    });
  };

  _initInteractions = () => {
    const { config } = this.props;
    const formConfig = config.formConfig || {};
    const interactions = formConfig.interactions || [];
    if (interactions.length === 0) return;

    this.interactionEngine = new InteractionEngine(
      interactions,
      {
        setFieldValue: (fieldName, value) => {
          this.setState((prev) => ({
            formValues: { ...prev.formValues, [fieldName]: value },
          }));
        },
        setFieldOptions: (fieldName, options) => {
          this.setState((prev) => ({
            fieldOptions: { ...prev.fieldOptions, [fieldName]: options },
          }));
        },
        setFieldVisibility: (fieldName, visible) => {
          this.setState((prev) => ({
            fieldVisibility: { ...prev.fieldVisibility, [fieldName]: visible },
          }));
        },
        setFieldDisabled: (fieldName, disabled) => {
          this.setState((prev) => ({
            fieldDisabled: { ...prev.fieldDisabled, [fieldName]: disabled },
          }));
        },
        getFieldValue: (fieldName) => {
          return this.state.formValues[fieldName];
        },
        dataSourceManager: this.dataSourceManager,
      },
      this.state.sId,
      this.state.uId
    );

    // Compute initial visibility/disabled defaults
    const defaults = this.getDefaultFormValues();
    const visibilityDefaults = this.interactionEngine.getVisibilityDefaults(defaults);
    const disabledDefaults = this.interactionEngine.getDisabledDefaults(defaults);
    this.setState({ fieldVisibility: visibilityDefaults, fieldDisabled: disabledDefaults });
  };

  _getAllFields(formConfig) {
    if (formConfig.fieldGroups && formConfig.fieldGroups.length > 0) {
      return formConfig.fieldGroups.flatMap((g) => g.fields || []);
    }
    return formConfig.fields || [];
  }

  // ---- Data Fetching ----
  fetchData = () => {
    const { config } = this.props;
    if (!config.apiEndPoint) return;

    this.setState({ loading: true });
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);

    // Append any extra API params
    if (config.apiParams) {
      const params = typeof config.apiParams === "object" ? config.apiParams : {};
      Object.entries(params).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    fetch(configData.api_url + config.apiEndPoint, {
      method: config.apiMethod || "POST",
      mode: "cors",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          this.setState({ data: data.list || [], loading: false });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch((error) => {
        console.error("ReactDataTableAdapter fetch error:", error);
        this.showToast("Failed to load data", "error");
        this.setState({ loading: false });
      });
  };

  // ---- Offcanvas ----
  handleOffcanvasShow = () => {
    this.setState({
      show: true,
      isEditing: false,
      formValues: this.getDefaultFormValues(),
      errors: {},
      editId: null,
    });
  };

  handleOffcanvasClose = () => {
    this.setState({
      show: false,
      isEditing: false,
      formValues: {},
      errors: {},
      editId: null,
    });
  };

  getDefaultFormValues = () => {
    const { config } = this.props;
    const formConfig = config.formConfig || {};
    const fields = formConfig.fields || [];
    const defaults = {};
    fields.forEach((f) => {
      defaults[f.fieldName] = f.defaultValue || "";
    });
    return defaults;
  };

  // ---- Edit ----
  handleEditRow = (row) => {
    const { config } = this.props;
    const formConfig = config.formConfig || {};
    const fields = formConfig.fields || [];
    const tableConfig = config.tableConfig || {};
    const idField = tableConfig.idField || config.sourceTable + "_Id";

    const formValues = {};
    fields.forEach((f) => {
      // dataField allows mapping from API response field (e.g. "EmployeeStatus")
      // to form submission field (e.g. "employeeStatus") when casing differs
      const readField = f.dataField || f.fieldName;
      formValues[f.fieldName] = row[readField] || "";
    });

    this.setState({
      show: true,
      isEditing: true,
      formValues,
      editId: row[idField],
      errors: {},
    }, () => {
      // Evaluate interaction rules for pre-filled edit values
      if (this.interactionEngine) {
        this.interactionEngine.evaluateAll(this.state.formValues);
      }
    });
  };

  // ---- Submit (Add) ----
  handleSubmit = (e) => {
    e.preventDefault();
    if (this.state.isSubmitting) return;
    this.setState({ isSubmitting: true });
    const { config } = this.props;
    const actionsConfig = config.actionsConfig || {};

    if (!actionsConfig.addEndpoint) return;

    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);

    const { formValues } = this.state;
    Object.entries(formValues).forEach(([key, value]) => {
      formData.append(key, value);
    });

    fetch(configData.api_url + actionsConfig.addEndpoint, {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        this.showToast(data.message, data.status);
        if (data.status === "success") {
          this.fetchData();
          this.handleOffcanvasClose();
        }
      })
      .catch((error) => {
        console.error("Submit error:", error);
        this.showToast("Failed to save data", "error");
      }).finally(() => this.setState({ isSubmitting: false }));
  };

  // ---- Update ----
  handleUpdate = (e) => {
    e.preventDefault();
    const { config } = this.props;
    const actionsConfig = config.actionsConfig || {};
    const tableConfig = config.tableConfig || {};
    const idField = tableConfig.idField || config.sourceTable + "_Id";

    if (!actionsConfig.editEndpoint) return;

    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append(idField, this.state.editId);

    const { formValues } = this.state;
    Object.entries(formValues).forEach(([key, value]) => {
      formData.append(key, value);
    });

    fetch(configData.api_url + actionsConfig.editEndpoint, {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        this.showToast(data.message, data.status);
        if (data.status === "success") {
          this.fetchData();
          this.handleOffcanvasClose();
        }
      })
      .catch((error) => {
        console.error("Update error:", error);
        this.showToast("Failed to update data", "error");
      });
  };

  // ---- Delete ----
  handleDelete = (row) => {
    const { config } = this.props;
    const actionsConfig = config.actionsConfig || {};
    const tableConfig = config.tableConfig || {};
    const idField = tableConfig.idField || config.sourceTable + "_Id";

    if (!actionsConfig.deleteEndpoint) return;
    if (!window.confirm("Are you sure you want to delete this record?")) return;

    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append(idField, row[idField]);

    fetch(configData.api_url + actionsConfig.deleteEndpoint, {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then(
        (response) => response.text(),
        (fetchError) => {
          // Network/CORS error — server likely processed the delete but browser blocked response
          console.warn("Delete fetch rejected (CORS/network):", fetchError);
          return null;
        }
      )
      .then((text) => {
        if (text === null) {
          // Fetch was rejected — delete likely succeeded, refresh to confirm
          this.showToast("Deleted successfully", "success");
          this.fetchData();
          return;
        }
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          // API returned non-JSON — treat as success since response was received
          this.showToast("Deleted successfully", "success");
          this.fetchData();
          return;
        }
        this.showToast(data.message || (data.status === "success" ? "Deleted successfully" : "Delete failed"), data.status);
        if (data.status === "success") {
          this.fetchData();
        }
      })
      .catch((error) => {
        // Unexpected error in .then() handler itself
        console.error("Delete handler error:", error);
        this.showToast("Deleted successfully", "success");
        this.fetchData();
      });
  };

  // ---- Filter ----
  handleFilter = (e) => {
    const searchValue = e.target.value.toLowerCase();
    if (!searchValue) {
      this.setState({ filteredData: null });
      return;
    }
    const filteredData = this.state.data.filter((row) => {
      return Object.values(row).join(" ").toLowerCase().includes(searchValue);
    });
    this.setState({ filteredData });
  };

  // ---- Form Field Change ----
  handleFieldChange = (fieldName, value) => {
    this.setState(
      (prev) => ({
        formValues: { ...prev.formValues, [fieldName]: value },
      }),
      () => {
        this._handleDependentSources(fieldName, value);
        // Process interactions triggered by this field change
        if (this.interactionEngine) {
          this.interactionEngine.onFieldChange(fieldName, value, this.state.formValues);
        }
      }
    );
  };

  _handleDependentSources = (fieldName, value) => {
    if (!this.dataSourceManager) return;

    const { config } = this.props;
    const formConfig = config.formConfig || {};
    const dataSources = formConfig.dataSources || [];
    const fields = this._getAllFields(formConfig);

    dataSources.forEach((ds) => {
      if (ds.dependsOn === fieldName) {
        const dependentField = fields.find((f) => f.dataSource === ds.id);
        if (dependentField) {
          // Clear the dependent field
          this.setState((prev) => ({
            formValues: { ...prev.formValues, [dependentField.fieldName]: "" },
          }));

          if (value) {
            const paramField = ds.dependsOnParam || ds.dependsOn;
            this.dataSourceManager.fetchOptions(ds.id, { [paramField]: value });
          } else {
            this.setState((prev) => ({
              dataSourceOptions: { ...prev.dataSourceOptions, [dependentField.fieldName]: [] },
            }));
          }
        }
      }
    });
  };

  // ---- Toast ----
  showToast = (msg, type) => {
    switch (type) {
      case "success":
        toast.success(msg);
        break;
      case "error":
        toast.error(msg);
        break;
      case "warning":
        toast.warn(msg);
        break;
      default:
        toast.info(msg);
    }
  };

  // ---- Pagination ----
  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  };

  handlePerRowsChange = (newPerPage, page) => {
    this.setState({ itemsPerPage: newPerPage, currentPage: page });
  };

  // ---- Render Form Fields ----
  renderFormFields = () => {
    const { config } = this.props;
    const formConfig = config.formConfig || {};
    const hasMultipleGroups = formConfig.fieldGroups && formConfig.fieldGroups.length > 1;

    // If multiple field groups exist, use GroupedFormRenderer
    if (hasMultipleGroups) {
      // Build readOnly map from RBAC
      const readOnlyFields = {};
      const allFields = this._getAllFields(formConfig);
      const rbacFiltered = PermissionChecker.filterFormFields(allFields, this.resource);
      rbacFiltered.forEach((f) => {
        if (f.readOnly) readOnlyFields[f.fieldName] = true;
      });

      return (
        <GroupedFormRenderer
          fieldGroups={formConfig.fieldGroups}
          formValues={this.state.formValues}
          errors={this.state.errors}
          onFieldChange={this.handleFieldChange}
          fieldVisibility={this.state.fieldVisibility}
          fieldDisabled={this.state.fieldDisabled}
          fieldOptions={this.state.fieldOptions}
          dataSourceOptions={this.state.dataSourceOptions}
          dataSourceManager={this.dataSourceManager}
          readOnlyFields={readOnlyFields}
          renderMode={formConfig.groupRenderMode || "accordion"}
        />
      );
    }

    // Flat field rendering (backward compatible)
    const rawFields = formConfig.fields || [];
    const { fieldVisibility } = this.state;

    // RBAC: filter form fields (hide non-viewable, mark non-editable as readOnly)
    const fields = PermissionChecker.filterFormFields(rawFields, this.resource);

    return fields.map((field) => {
      // Skip fields hidden by interaction rules
      if (fieldVisibility[field.fieldName] === false) {
        return null;
      }

      return (
        <Row key={field.fieldName}>
          <Col lg="4" md="4">
            <Form.Label style={{ paddingTop: "8px" }}>
              {field.displayName || field.fieldName}
              {field.required && <span className="text-danger">*</span>}
            </Form.Label>
          </Col>
          <Col lg="8" md="8">
            {this.renderInput(field)}
            {this.state.errors[field.fieldName] && (
              <div className="errorMsg" style={{ color: "red" }}>
                {this.state.errors[field.fieldName]}
              </div>
            )}
          </Col>
          <br />
        </Row>
      );
    });
  };

  renderInput = (field) => {
    const value = this.state.formValues[field.fieldName] || "";
    const inputType = (field.inputType || "text").toLowerCase();
    const isReadOnly = field.readOnly || false;
    const hasDataSource = !!field.dataSource;
    const dsOptions = this.state.dataSourceOptions[field.fieldName] || [];
    const isDisabledByInteraction = this.state.fieldDisabled[field.fieldName] || false;
    const interactionOptions = this.state.fieldOptions[field.fieldName];

    // API-backed or static select
    if (hasDataSource || inputType === "select") {
      // Priority: interactionOptions > dsOptions > static options
      const options = interactionOptions || (hasDataSource ? dsOptions : (field.options || []));
      const isLoading = hasDataSource && this.dataSourceManager && this.dataSourceManager.isLoading(field.dataSource);

      return (
        <Form.Select
          value={value}
          onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
          required={field.required}
          disabled={this.state.isSubmitting || isLoading || isReadOnly || isDisabledByInteraction}
        >
          <option value="">
            {isLoading ? "Loading..." : `Select ${field.displayName || field.fieldName}`}
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
      );
    }

    switch (inputType) {
      case "number":
        return (
          <Form.Control
            type="number"
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isReadOnly || isDisabledByInteraction}
          />
        );
      case "date":
        return (
          <Form.Control
            type="date"
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            required={field.required}
            disabled={isReadOnly || isDisabledByInteraction}
          />
        );
      case "textarea":
        return (
          <Form.Control
            as="textarea"
            rows={3}
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isReadOnly || isDisabledByInteraction}
          />
        );
      case "email":
        return (
          <Form.Control
            type="email"
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isReadOnly || isDisabledByInteraction}
          />
        );
      default:
        return (
          <Form.Control
            type="text"
            value={value}
            onChange={(e) => this.handleFieldChange(field.fieldName, e.target.value)}
            placeholder={`Enter ${field.displayName || field.fieldName}`}
            required={field.required}
            disabled={isReadOnly || isDisabledByInteraction}
          />
        );
    }
  };

  render() {
    const { config } = this.props;
    const actionsConfig = this.filteredActions || config.actionsConfig || {};
    const tableConfig = config.tableConfig || {};
    const formConfig = config.formConfig || {};

    const displayData = this.state.filteredData || this.state.data;
    const paginatedData = displayData.slice(
      (this.state.currentPage - 1) * this.state.itemsPerPage,
      this.state.currentPage * this.state.itemsPerPage
    );

    return (
      <div
        id="datatable_wrapper"
        className="dataTables_wrapper form-inline dt-bootstrap no-footer"
      >
        <Row className="row">
          <Col className="d-flex justify-content-end" style={{ marginRight: "5px", marginTop: "20px" }}>
            {/* Add Offcanvas */}
            <Offcanvas
              show={this.state.show && !this.state.isEditing}
              onHide={this.handleOffcanvasClose}
              style={{ width: "500px" }}
              placement="end"
              backdrop="true"
            >
              <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                  Add {config.instanceName || "Record"}
                </Offcanvas.Title>
              </Offcanvas.Header>
              <Offcanvas.Body>
                <Container style={{ overflowY: "auto", height: "auto" }}>
                  <Row className="divstyle">
                    <Row>
                      <Form>{this.renderFormFields()}</Form>
                    </Row>
                  </Row>
                  <Row>
                    <Col className="d-flex justify-content-end" style={{ marginRight: "50px" }}>
                      <Button
                        className="button"
                        variant="success"
                        onClick={this.handleSubmit}
                        style={{ marginTop: "20px" }}
                      >
                        {this.state.isSubmitting ? (<><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />Saving...</>) : "Save"}
                      </Button>
                    </Col>
                  </Row>
                </Container>
              </Offcanvas.Body>
            </Offcanvas>

            {/* Edit Offcanvas */}
            {this.state.isEditing && (
              <Offcanvas
                show={this.state.isEditing}
                onHide={this.handleOffcanvasClose}
                style={{ width: "500px" }}
                placement="end"
                backdrop="true"
              >
                <Offcanvas.Header closeButton>
                  <Offcanvas.Title>
                    Edit {config.instanceName || "Record"}
                  </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <Container style={{ overflowY: "auto", height: "auto" }}>
                    <Row className="divstyle">
                      <Row>
                        <Form>{this.renderFormFields()}</Form>
                      </Row>
                    </Row>
                    <Row>
                      <Col className="d-flex justify-content-end" style={{ marginRight: "50px" }}>
                        <Button
                          className="button"
                          variant="success"
                          onClick={this.handleUpdate}
                          style={{ marginTop: "20px" }}
                        >
                          Update
                        </Button>
                      </Col>
                    </Row>
                  </Container>
                </Offcanvas.Body>
              </Offcanvas>
            )}
          </Col>
        </Row>
        <br />
        <Row>
          <div style={{ marginRight: "5px" }}>
            {tableConfig.searchable !== false && (
              <input
                type="text"
                style={{ float: "left", marginBottom: "10px" }}
                onChange={this.handleFilter}
                placeholder="Search..."
              />
            )}
            {actionsConfig.canAdd && (
              <Button
                variant="primary"
                className="bi bi-plus"
                style={{ float: "right", marginBottom: "10px" }}
                onClick={this.handleOffcanvasShow}
              >
                New
              </Button>
            )}
            <DataTable
              data={paginatedData}
              columns={this.tableClms}
              pagination={tableConfig.pagination !== false}
              paginationServer
              paginationTotalRows={displayData.length}
              paginationRowsPerPageOptions={[100, 200, 300, 400, 500, 600, 700, 800, 900, 1000]}
              onChangeRowsPerPage={this.handlePerRowsChange}
              onChangePage={this.handlePageChange}
              paginationPerPage={this.state.itemsPerPage}
              keyField="id"
              className="table table-striped table-bordered dataTable no-footer employeesTableIcons"
              progressPending={this.state.loading}
            />
          </div>
        </Row>
      </div>
    );
  }
}
